import type { As, PropsOf, SystemStyleObject } from '@chakra-ui/react';
import { chakra, useStyleConfig } from '@chakra-ui/react';
import React from 'react';

import { withChakraAs } from '../../utils/withChakraAs';

type HeadingThemeProps =
  | {
      /**
       * The rendered style of the text.
       *
       * @default heading
       */
      variant?: 'heading';

      /**
       * The size of the text.
       *
       * @default lg
       */
      size?: '3xl' | '2xl' | 'xl' | 'lg' | 'md';
    }
  | {
      /**
       * The rendered style of the text.
       *
       * @default heading
       */
      variant: 'title';
      size?: never;
    }
  | {
      /**
       * The rendered style of the text.
       *
       * @default heading
       */
      variant: 'subtitle';
      size?: never;
    };

export type HeadingProps<Component extends As = 'h2'> = PropsOf<Component> &
  HeadingThemeProps & {
    /**
     * Additional style config needed for component that wraps `Heading`. This
     * should only be used internally by other Orbit components. Please use
     * `className` for all styling.
     *
     * @internal
     */
    __orbitStyleConfig?: SystemStyleObject;
  };

export const Heading = withChakraAs<HeadingProps, 'h2'>(
  ({ variant = 'heading', size = 'lg', __orbitStyleConfig, ...props }) => {
    const defaultRole =
      !props.as || (typeof props.as === 'string' && props.as.match(/^h[1-6]$/))
        ? undefined
        : 'heading';
    const styles = useStyleConfig('Heading', { variant, size });

    return (
      <chakra.h2
        role={defaultRole}
        {...props}
        __css={{ ...styles, ...__orbitStyleConfig }}
      />
    );
  },
);
